// Adivinhar uma palavra — contra o relógio (IDEA-025 v3, ecrã próprio na v4).
//
// Antes eram três tentativas e a palavra acabava, acertada ou não. Agora há um
// cronómetro e tentativas à vontade: enganares-te custa segundos, não uma vida.
// A resposta aparece como traços, um por letra, e cada pista descobre a letra
// seguinte por mais dez segundos no relógio.
//
// Os traços vêm da API já feitos (`mascara`). Mandar a palavra e escondê-la aqui
// punha a resposta a um painel de devtools de distância — é exactamente o erro
// que services/games.ts foi escrito para não repetir.
//
// Adivinhar é um ECRÃ, não um pop-up. Era o único jogo que abria uma janela por
// cima da página, com a navegação toda ainda por baixo dela; os outros três
// levam o ecrã inteiro enquanto se joga. Agora este também: mesma barra, mesmo
// relógio no mesmo sítio, e o mesmo "Sair" que pergunta antes de custar a ronda.

import { useEffect, useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AFTER_GAME_CHANGE, giveUpWord, spendHint, submitGuess } from "@/api/mutations";
import { ApiError } from "@/api/client";
import { themeInfo as infoTema } from "@historia/shared/themes";
import { HINT_PENALTY_MS } from "@historia/shared/validation";
import type { VisibleWordWithGuesses as PalavraComPalpites } from "@historia/shared/contracts";
import { formatarTempo, useCronometro } from "@/components/jogos/RondaCronometrada";
import { BarraDeJogo } from "@/components/jogos/BarraDeJogo";
import { Confirmacao } from "@/components/ui/Confirmacao";
import { useOutroNome } from "@/components/jogos/useOutroNome";

const campo =
  "rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-3 text-center text-lg font-semibold uppercase tracking-[0.2em] text-tinta outline-none transition-colors placeholder:tracking-normal placeholder:text-tinta-700/40 focus:border-ouro focus:ring-2 focus:ring-ouro/30";

const PENALIZACAO_S = Math.round(HINT_PENALTY_MS / 1000);

/** Tempo já gasto na palavra: o que a API guardou + o que as pistas custaram. */
function tempoDaPalavra(p: PalavraComPalpites): number {
  return (p.tempo_ms ?? 0) + p.pistas * HINT_PENALTY_MS;
}

export function Adivinhar({
  palavras,
  aoAbrir,
}: {
  palavras: PalavraComPalpites[];
  aoAbrir: (id: string) => void;
}) {
  const outro = useOutroNome();

  if (palavras.length === 0) {
    return (
      <p className="rounded-xl bg-creme-50 px-4 py-6 text-center text-sm text-tinta-600">
        {outro} ainda não pôs nenhuma palavra. Volta daqui a pouco. 💛
      </p>
    );
  }

  return (
    <section>
      <p className="mb-4 text-sm text-tinta-600">
        A palavra de {outro}, em emojis. Toca para adivinhar — o relógio arranca aí.
        Tenta as vezes que quiseres; cada pista descobre uma letra e soma{" "}
        {PENALIZACAO_S}s. Ganha a ronda quem for mais rápido.
      </p>

      <ul className="flex flex-col gap-2">
        {palavras.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => aoAbrir(p.id)}
              className="flex w-full items-center justify-between rounded-2xl border border-ouro/20 bg-creme-50 px-4 py-3 text-left transition-colors hover:border-ouro/40 hover:bg-creme-100"
            >
              <span className="flex items-center gap-3">
                <span className="text-2xl" aria-hidden>
                  {p.emojis}
                </span>
                <span className="text-xs text-tinta-500">
                  {infoTema(p.tema).icon} {infoTema(p.tema).label}
                </span>
              </span>
              <Etiqueta palavra={p} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Etiqueta({ palavra }: { palavra: PalavraComPalpites }) {
  if (palavra.acertou_na !== null) {
    return (
      <span className="text-sm font-semibold text-emerald-600">
        {formatarTempo(tempoDaPalavra(palavra))} 🎉
      </span>
    );
  }
  if (palavra.revelada) return <span className="text-sm text-tinta-500">Revelada</span>;
  return <span className="text-sm text-ouro-600">Adivinhar →</span>;
}

/**
 * Os traços: uma casa por caractere.
 *
 * Cada casa é uma caixa de largura fixa com um risco por baixo, em vez do
 * caractere "_": o underscore assenta abaixo da linha das letras, e uma fila
 * meia sublinhada meia com letras ficava aos altos. Assim as letras que as
 * pistas descobrem aparecem alinhadas por cima do próprio risco.
 */
function Mascara({ mascara }: { mascara: string }) {
  const casas = [...mascara];
  const letras = casas.filter((c) => c !== " ").length;

  return (
    <p
      className="flex flex-wrap items-end justify-center gap-x-1.5 gap-y-3"
      aria-label={`Resposta com ${letras} caracteres`}
    >
      {casas.map((ch, i) =>
        ch === " " ? (
          // O espaço entre palavras: largo e sem risco, para se ver a forma.
          <span key={i} className="w-4" aria-hidden />
        ) : (
          <span
            key={i}
            aria-hidden
            className={`flex h-9 w-6 items-center justify-center border-b-2 text-2xl font-semibold uppercase ${
              // tinta-500/40 e não um tinta-300: a paleta desta app pára no 500
              // (globals.css) e uma cor que não existe deixa a borda em
              // currentColor — foi assim que os traços por descobrir
              // desapareceram do ecrã na primeira tentativa.
              ch === "_" ? "border-tinta-500/40" : "border-ouro text-ouro-600"
            }`}
          >
            {ch === "_" ? "" : ch}
          </span>
        ),
      )}
    </p>
  );
}


/**
 * O ecrã de adivinhar uma palavra.
 *
 * `elaJaJogou` decide o que "Sair" faz, e é a mesma pergunta dos outros jogos
 * feita nos termos deste: a palavra que EU escrevi já foi resolvida? Se sim, a
 * outra pessoa já fez a parte dela e sair agora é desistir da ronda. Se não,
 * ninguém está à espera e sair é só sair.
 */
export function EcraPalpite({
  palavra,
  elaJaJogou,
  aoFechar,
}: {
  palavra: PalavraComPalpites;
  elaJaJogou: boolean;
  aoFechar: () => void;
}) {
  const queryClient = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [quase, setQuase] = useState(false);
  const [palpite, setPalpite] = useState("");
  const [aConfirmarSaida, setAConfirmarSaida] = useState(false);
  const [aEnviar, iniciar] = useTransition();
  const cronometro = useCronometro();

  const acertou = palavra.acertou_na !== null;
  const terminada = acertou || palavra.revelada || palavra.desistiu;

  // O relógio arranca ao abrir o ecrã e pára quando a palavra fica feita. Só
  // conta enquanto o ecrã está aberto: sair e voltar recomeça a contagem, como
  // nos tabuleiros. São duas pessoas em confiança — não há aqui nada a defender,
  // e um relógio a correr a noite toda seria pior.
  useEffect(() => {
    if (!terminada) cronometro.arrancar();
  }, [terminada, cronometro]);

  const decorrido = terminada
    ? tempoDaPalavra(palavra)
    : cronometro.decorrido + palavra.pistas * HINT_PENALTY_MS;

  async function recarregar() {
    for (const key of AFTER_GAME_CHANGE) {
      await queryClient.invalidateQueries({ queryKey: key });
    }
  }

  function palpitar(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setErro(null);
    setQuase(false);

    // Medido antes do await (a viagem à API não conta) e com `medir()`, não com
    // `decorrido`: este só anda de 250 em 250ms e um palpite imediato ia a 0.
    const tempoMs = cronometro.medir();

    iniciar(async () => {
      try {
        const resultado = await submitGuess({ wordId: palavra.id, texto: palpite, tempoMs });
        setPalpite("");
        if (resultado.correct) cronometro.parar();
        // Errado mas perto: um empurrão simpático (custa segundos, não uma vida).
        if (resultado.nearMiss) setQuase(true);
        await recarregar();
      } catch (error) {
        setErro(error instanceof ApiError ? error.message : "Não foi possível enviar o palpite.");
      }
    });
  }

  function pedirPista() {
    setErro(null);
    setQuase(false);
    iniciar(async () => {
      try {
        await spendHint(palavra.id);
        await recarregar();
      } catch (error) {
        setErro(error instanceof ApiError ? error.message : "Não foi possível pedir a pista.");
      }
    });
  }

  function pedirSaida() {
    // Já feita, ou ninguém à espera: sair é só sair.
    if (terminada || !elaJaJogou) {
      cronometro.parar();
      aoFechar();
      return;
    }
    setAConfirmarSaida(true);
  }

  function desistir() {
    const tempoMs = cronometro.parar();
    iniciar(async () => {
      try {
        await giveUpWord({ wordId: palavra.id, tempoMs: Math.max(1, tempoMs) });
        await recarregar();
      } catch (error) {
        setErro(error instanceof ApiError ? error.message : "Não foi possível desistir.");
      } finally {
        setAConfirmarSaida(false);
        aoFechar();
      }
    });
  }

  return (
    <div className="mx-auto flex max-w-sm flex-col items-center px-4 py-6">
      <BarraDeJogo tempoMs={decorrido} aoSair={pedirSaida} />

      <p className="mb-1 text-sm font-semibold text-ouro-600">
        {infoTema(palavra.tema).icon} {infoTema(palavra.tema).label}
      </p>

      <div className="my-5 text-center text-6xl" aria-hidden>
        {palavra.emojis}
      </div>

      <div className="my-4">
        <Mascara mascara={palavra.mascara} />
      </div>

      {terminada ? (
        <div className="text-center">
          <p className="text-3xl" aria-hidden>
            {acertou ? "🎉" : "💡"}
          </p>
          <p className="mt-2 text-sm text-tinta-600">
            {acertou
              ? `Acertaste à ${palavra.acertou_na}.ª, em ${formatarTempo(tempoDaPalavra(palavra))}`
              : palavra.desistiu
                ? "Desististe. A palavra era"
                : "A palavra era"}
          </p>
          {!acertou && (
            <p className="mt-1 font-titulo text-3xl text-tinta-900">{palavra.palavra}</p>
          )}
          {palavra.pistas > 0 && (
            <p className="mt-1 text-xs text-tinta-500">
              {palavra.pistas} {palavra.pistas === 1 ? "pista" : "pistas"} · +
              {palavra.pistas * PENALIZACAO_S}s
            </p>
          )}
          <button
            type="button"
            onClick={aoFechar}
            className="mt-6 rounded-full bg-ouro px-6 py-2.5 font-semibold text-creme-50 transition-colors hover:bg-ouro-600"
          >
            Voltar
          </button>
        </div>
      ) : (
        <form onSubmit={palpitar} className="flex w-full flex-col gap-2">
          <input
            value={palpite}
            // Sempre em maiúsculas: é mais fácil de ler e casa com os traços.
            onChange={(e) => setPalpite(e.target.value.toUpperCase())}
            required
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="O teu palpite…"
            className={campo}
          />
          <button
            type="submit"
            disabled={aEnviar}
            className="rounded-full bg-ouro px-5 py-2.5 font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:opacity-60"
          >
            {aEnviar ? "…" : "Adivinhar"}
          </button>

          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-xs text-tinta-500">
              {palavra.palpites.length}{" "}
              {palavra.palpites.length === 1 ? "tentativa" : "tentativas"}
              {palavra.pistas > 0 && ` · +${palavra.pistas * PENALIZACAO_S}s em pistas`}
            </span>
            <button
              type="button"
              onClick={pedirPista}
              disabled={aEnviar || palavra.pistas_restantes === 0}
              title={
                palavra.pistas_restantes === 0
                  ? "A última letra é contigo"
                  : `Descobre a próxima letra (+${PENALIZACAO_S}s)`
              }
              className="shrink-0 rounded-full border border-ouro/40 px-3 py-1.5 text-xs font-semibold text-ouro-600 transition-colors hover:bg-ouro/10 disabled:opacity-40"
            >
              💡 Pista +{PENALIZACAO_S}s
            </button>
          </div>

          {palavra.palpites.length > 0 && (
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {palavra.palpites.map((g) => (
                <li
                  key={g.id}
                  className="rounded-full bg-creme-100 px-2.5 py-0.5 text-xs text-tinta-500 line-through"
                >
                  {g.texto}
                </li>
              ))}
            </ul>
          )}

          {quase && (
            <p className="mt-1 text-center text-sm font-medium text-ouro-600">
              Quase! 💛 Falha uma letra — confere letra a letra.
            </p>
          )}
          {erro && <p className="mt-1 text-center text-sm text-rose-600">{erro}</p>}
        </form>
      )}

      <Confirmacao
        aberto={aConfirmarSaida}
        destrutivo
        aProcessar={aEnviar}
        titulo="Desistir da ronda?"
        mensagem="A outra pessoa já adivinhou a tua palavra. Se saíres agora, desistes desta e a ronda fecha com a vitória dela."
        textoConfirmar="Desistir"
        textoCancelar="Continuar a tentar"
        onCancelar={() => setAConfirmarSaida(false)}
        onConfirmar={desistir}
      />
    </div>
  );
}
